import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, BookOpen, CheckCircle } from 'lucide-react';
import { LocationSelector } from '@/components/LocationSelector';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    parent_first_name: '',
    parent_last_name: '',
    parent_role: '',
    email: '',
    phone: '',
    location_type: 'DC Ward',
    location_detail: '',
    ward_number: '',
    city_county: '',
    virginia_city: '',
    virginia_county: '',
    maryland_city: '',
    maryland_county: '',
    address: '',
    children: [{
      name: '',
      age: '',
      gender: '',
      learning_stage: '',
      reading_level: '',
      interests: ''
    }],
    already_has_bookshelf: false,
    books_from_birth_signup: false,
    books_count: '0-10',
    annual_income: '',
    photo_consent: false,
    referral_source: ''
  });

  const parentRoles = [
    { value: '', label: 'Select role' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Father', label: 'Father' },
    { value: 'Guardian', label: 'Guardian' },
    { value: 'Grandparent', label: 'Grandparent' },
    { value: 'Other', label: 'Other' }
  ];

  const learningStages = [
    'Not Yet in School',
    'Pre-K',
    'Kindergarten',
    'Elementary (1st-5th)',
    'Middle School',
    'High School'
  ];

  const readingLevels = [
    'Not Reading Yet',
    'Beginning Reader',
    'Developing Reader',
    'Independent Reader'
  ];

  const referralSources = [
    'Word of Mouth',
    'Social Media',
    'Community Event',
    'School',
    'Library',
    'Church/Place of Worship',
    'Friend',
    'Online Search',
    'Other'
  ];

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, {
        name: '',
        age: '',
        gender: '',
        learning_stage: '',
        reading_level: '',
        interests: ''
      }]
    });
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData({ ...formData, children: newChildren });
    }
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setFormData({ ...formData, children: newChildren });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build location_detail based on location_type
      let location_detail = formData.location_detail;
      if (formData.location_type === 'DC Ward' && formData.ward_number) {
        location_detail = `Ward ${formData.ward_number}`;
      } else if (formData.location_type === 'Virginia') {
        location_detail = formData.virginia_city || formData.virginia_county || '';
      } else if (formData.location_type === 'Maryland') {
        location_detail = formData.maryland_city || formData.maryland_county || '';
      }

      const submitData = {
        ...formData,
        location_detail,
        children: formData.children.map(child => ({
          ...child,
          age: parseInt(child.age) || 0
        }))
      };

      await axios.post(`${API_URL}/api/public/register`, submitData);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-green-200 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Registration Submitted!</h2>
            <p className="text-stone-600 mb-6">
              Thank you for registering with The Bookshelf Project! Our team will review your application and you'll receive an email confirmation soon.
            </p>
            <div className="bg-green-50 rounded-lg p-4 text-left">
              <p className="text-sm text-green-800 font-medium mb-2">What happens next?</p>
              <ol className="text-sm text-green-700 space-y-2 list-decimal list-inside">
                <li>Our team will review your registration</li>
                <li>You'll receive an email when approved</li>
                <li>You can then schedule your bookshelf delivery</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="w-10 h-10 text-green-700" />
            <h1 className="text-3xl font-bold text-green-800">The Bookshelf Project</h1>
          </div>
          <p className="text-lg text-stone-600">Register for your free bookshelf and books!</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Parent Information */}
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Parent/Guardian Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_first_name">First Name *</Label>
                  <Input
                    id="parent_first_name"
                    value={formData.parent_first_name}
                    onChange={(e) => setFormData({ ...formData, parent_first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="parent_last_name">Last Name *</Label>
                  <Input
                    id="parent_last_name"
                    value={formData.parent_last_name}
                    onChange={(e) => setFormData({ ...formData, parent_last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="parent_role">Your Role *</Label>
                <select
                  id="parent_role"
                  value={formData.parent_role}
                  onChange={(e) => setFormData({ ...formData, parent_role: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                  required
                >
                  {parentRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Information */}
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Location Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <LocationSelector formData={formData} setFormData={setFormData} required={true} />
              
              <div>
                <Label htmlFor="address">Full Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, city, state, zip"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Children Information */}
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Children Information</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addChild}>
                <Plus className="w-4 h-4 mr-2" />
                Add Child
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.children.map((child, index) => (
                <div key={index} className="p-4 bg-stone-50 rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Child {index + 1}</h4>
                    {formData.children.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeChild(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Child's Name *</Label>
                      <Input
                        value={child.name}
                        onChange={(e) => updateChild(index, 'name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Age *</Label>
                      <Input
                        type="number"
                        min="0"
                        max="18"
                        value={child.age}
                        onChange={(e) => updateChild(index, 'age', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Gender</Label>
                      <select
                        value={child.gender}
                        onChange={(e) => updateChild(index, 'gender', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label>Learning Stage</Label>
                      <select
                        value={child.learning_stage}
                        onChange={(e) => updateChild(index, 'learning_stage', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200"
                      >
                        <option value="">Select</option>
                        {learningStages.map(stage => (
                          <option key={stage} value={stage}>{stage}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Reading Level</Label>
                      <select
                        value={child.reading_level}
                        onChange={(e) => updateChild(index, 'reading_level', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200"
                      >
                        <option value="">Select</option>
                        {readingLevels.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Interests</Label>
                      <Input
                        value={child.interests}
                        onChange={(e) => updateChild(index, 'interests', e.target.value)}
                        placeholder="e.g., animals, space, sports"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Books Currently at Home</Label>
                  <select
                    value={formData.books_count}
                    onChange={(e) => setFormData({ ...formData, books_count: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200"
                  >
                    <option value="0-10">0-10 books</option>
                    <option value="11-25">11-25 books</option>
                    <option value="26-50">26-50 books</option>
                    <option value="50+">More than 50 books</option>
                  </select>
                </div>
                <div>
                  <Label>How did you hear about us?</Label>
                  <select
                    value={formData.referral_source}
                    onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200"
                  >
                    <option value="">Select</option>
                    {referralSources.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="already_has_bookshelf"
                    checked={formData.already_has_bookshelf}
                    onCheckedChange={(checked) => setFormData({ ...formData, already_has_bookshelf: checked })}
                  />
                  <Label htmlFor="already_has_bookshelf" className="font-normal cursor-pointer">
                    We already have a bookshelf at home
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="books_from_birth_signup"
                    checked={formData.books_from_birth_signup}
                    onCheckedChange={(checked) => setFormData({ ...formData, books_from_birth_signup: checked })}
                  />
                  <Label htmlFor="books_from_birth_signup" className="font-normal cursor-pointer">
                    We are enrolled in Books from Birth / Imagination Library
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="photo_consent"
                    checked={formData.photo_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, photo_consent: checked })}
                  />
                  <Label htmlFor="photo_consent" className="font-normal cursor-pointer">
                    I consent to photos being taken during delivery for social media/promotional use
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg bg-green-700 hover:bg-green-800"
          >
            {loading ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Submitting...
              </>
            ) : (
              'Submit Registration'
            )}
          </Button>

          <p className="text-center text-sm text-stone-500 mt-4">
            By submitting, you agree to be contacted by The Bookshelf Project regarding your bookshelf delivery.
          </p>
        </form>
      </div>
    </div>
  );
}
